import { useEffect, useState } from 'react';
import type { Category } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { categoriesApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';
import { TilePreview } from '../../components/TilePreview.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { CategoryFormModal } from './CategoryFormModal.js';

export function CategoriesPage() {
  const { t } = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Category | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setCategories((await categoriesApi.list()).sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('categories.title')}</h1>
          <p>{t('categories.subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
          {t('categories.new')}
        </button>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('categories.preview')}</th>
                <th>{t('categories.name')}</th>
                <th>{t('categories.color')}</th>
                <th className="numeric">{t('categories.sortOrder')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id}>
                  <td>
                    <TilePreview label={c.name} color={c.color} imageUrl={c.imageUrl} size={48} />
                  </td>
                  <td>{c.name}</td>
                  <td className="numeric">{c.color}</td>
                  <td className="numeric">{c.sortOrder}</td>
                  <td>
                    <span className={`badge badge--${c.active ? 'ok' : 'neutral'}`}>
                      {c.active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(c)}>
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setDeactivateTarget(c)}
                    >
                      {c.active ? t('categories.deactivate') : t('categories.activate')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CategoryFormModal
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setCategories((prev) => {
              const exists = prev.some((c) => c.id === saved.id);
              return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [...prev, saved];
            });
            setEditing(null);
          }}
        />
      )}

      {deactivateTarget && (
        <ConfirmDialog
          title={deactivateTarget.active ? t('categories.deactivate') : t('categories.activate')}
          consequence={t('categories.deactivateConfirm')}
          danger={deactivateTarget.active}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={async () => {
            const target = deactivateTarget;
            setDeactivateTarget(null);
            try {
              const updated = target.active
                ? await categoriesApi.deactivate(target.id)
                : await categoriesApi.activate(target.id);
              setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
            }
          }}
        />
      )}
    </div>
  );
}
